import React, { useEffect, useState } from "react";
import api, { downloadAccrualExport } from "../../api";
import { toast } from "sonner";
import { CalendarClock, Play, ArrowDownToLine, BellRing, ChevronDown, ChevronRight, FileSpreadsheet, FileText, TrendingUp, Mail, Save, CalendarRange, Settings2 } from "lucide-react";
import { fmtDate } from "./adminUtils";
import { ConfirmDialog } from "./ConfirmDialog";

const monthLabel = (period) => {
  if (!period) return "—";
  const parts = period.split("-");
  if (parts.length < 2) return `Anno ${parts[0]}`; // dotazione annua: solo l'anno
  const [y, m] = parts;
  const names = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  return `${names[Number(m) - 1] || m} ${y}`;
};

const TYPE_BADGE = {
  carryover: { label: "Riporto", cls: "bg-blue-100 text-blue-700" },
  annual: { label: "Dotazione annua", cls: "bg-purple-100 text-purple-700" },
  monthly: { label: "Rateo mensile", cls: "bg-green-100 text-green-700" },
};

export const AccrualsTab = () => {
  const [runs, setRuns] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'run' | 'carryover' | 'reminders'
  const [runPeriod, setRunPeriod] = useState("");
  const [hrEmail, setHrEmail] = useState("");
  const [savingHr, setSavingHr] = useState(false);
  const [mode, setMode] = useState("monthly");
  const [savingMode, setSavingMode] = useState(false);
  const [annualYear, setAnnualYear] = useState(String(new Date().getFullYear()));
  const [annualProrate, setAnnualProrate] = useState(true);

  const load = () => api.get("/accruals").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings/hr").then((r) => setHrEmail(r.data?.hr_email || "")).catch(() => {});
    api.get("/accruals/settings").then((r) => setMode(r.data?.mode || "monthly")).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const saveMode = async (newMode) => {
    setSavingMode(true);
    try {
      await api.put("/accruals/settings", { mode: newMode });
      setMode(newMode);
      toast.success(newMode === "annual" ? "Modalità impostata su Dotazione annua" : "Modalità impostata su Rateo mensile");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSavingMode(false); }
  };

  const saveHrEmail = async () => {
    setSavingHr(true);
    try {
      await api.put("/settings/hr", { hr_email: hrEmail.trim() });
      toast.success(hrEmail.trim() ? "Email ufficio HR salvata" : "Email ufficio HR rimossa");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSavingHr(false); }
  };

  const doAction = async () => {
    const action = confirm;
    setBusy(true);
    try {
      if (action === "run") {
        const res = await api.post("/accruals/run", runPeriod ? { period: runPeriod } : {});
        if (res.data?.skipped) toast.info(`Maturazione già eseguita per ${runPeriod || "questo mese"}`);
        else toast.success(`Maturazione eseguita: ${res.data?.run?.employees_count ?? 0} dipendenti`);
      } else if (action === "annual") {
        const res = await api.post("/accruals/annual", { year: Number(annualYear) || undefined, prorate: annualProrate });
        if (res.data?.skipped) toast.info(`Dotazione annua già accreditata per l'anno ${annualYear}`);
        else toast.success(`Dotazione annua accreditata: ${res.data?.run?.employees_count ?? 0} dipendenti`);
      } else if (action === "carryover") {
        const res = await api.post("/accruals/carryover", {});
        toast.success(`Saldi riportati: ${res.data?.run?.employees_count ?? 0} dipendenti`);
      } else if (action === "reminders") {
        const res = await api.post("/accruals/reminders/run");
        toast.success(`Solleciti inviati: ${res.data?.sent ?? 0}`);
      } else if (action === "scatti") {
        const res = await api.post("/accruals/scatti/run");
        toast.success(`Scatti elaborati: ${res.data?.processed ?? 0} dipendenti con nuovi scatti`);
      }
      setConfirm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const exportRun = async (run, format) => {
    try {
      await downloadAccrualExport(run.id, format, `maturazione_${run.type}_${run.period}.${format}`);
      toast.success(`Export ${format.toUpperCase()} scaricato`);
    } catch { toast.error("Errore durante l'export"); }
  };

  const confirmText = {
    run: `Verrà accreditato il rateo mensile (ferie, ROL, ex-festività) per ${runPeriod || "il mese corrente"}, calcolato dal CCNL di ciascun dipendente e riproporzionato per il part-time. L'operazione è protetta contro il doppio accredito nello stesso mese.`,
    annual: `Verrà accreditato l'intero monte annuo (ferie, ROL, ex-festività) per l'anno ${annualYear} a ogni dipendente con CCNL assegnato, calcolato dal CCNL e riproporzionato per il part-time${annualProrate ? " e per i mesi restanti dell'anno in caso di assunzione infrannuale" : " (dotazione intera anche per gli assunti in corso d'anno)"}. L'operazione è idempotente: non accredita due volte lo stesso anno.`,
    carryover: "Verranno sommati una-tantum i saldi del periodo precedente ai saldi correnti di ogni dipendente idoneo, e i campi 'periodo precedente' verranno azzerati. Operazione non reversibile.",
    reminders: "Verrà inviato un sollecito via email ai dipendenti che hanno ancora la password temporanea da oltre 3 giorni.",
    scatti: "Verranno rilevati i nuovi scatti di anzianità maturati (in base al CCNL), registrati nello storico di ogni dipendente e notificati all'ufficio HR via email. Operazione idempotente (non ri-registra scatti già presenti).",
  };

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 flex items-start gap-3" data-testid="accruals-info">
        <CalendarClock size={20} className="text-[#0d6cb8] mt-0.5 shrink-0" />
        <div className="text-[13px] text-gray-700">
          <b className="text-[#16345c]">Maturazione automatica</b> — {mode === "annual" ? (
            <>La <b>dotazione annua</b> di ferie, ROL ed ex-festività viene accreditata automaticamente il <b>1° gennaio</b> di ogni anno (intero monte annuo CCNL, riproporzionato per il % part-time e per i mesi restanti in caso di assunzione infrannuale).</>
          ) : (
            <>Il rateo mensile di ferie, ROL ed ex-festività viene accreditato automaticamente il <b>16 di ogni mese</b> ai dipendenti con un CCNL assegnato (annuale ÷ 12, riproporzionato per il % part-time).</>
          )} Usa i pulsanti qui sotto per operazioni manuali o di recupero.
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5" data-testid="accrual-mode-card">
        <div className="flex items-center gap-2 text-[13px] font-bold text-[#16345c] mb-1"><Settings2 size={16} className="text-[#0d6cb8]" /> Modalità di maturazione</div>
        <p className="text-[12px] text-gray-500 mb-3">Scegli come accreditare automaticamente ferie/ROL/ex-festività: mese per mese (rateo) oppure tutto l'anno in un'unica soluzione (dotazione annua).</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => saveMode("monthly")} disabled={savingMode} data-testid="accrual-mode-monthly" className={`flex items-center gap-2 text-[13px] font-bold px-4 py-2 rounded-sm border transition-colors disabled:opacity-60 ${mode === "monthly" ? "bg-[#16345c] text-white border-[#16345c]" : "bg-white text-gray-600 border-gray-300 hover:border-[#16345c]"}`}><CalendarClock size={15} /> Rateo mensile</button>
          <button onClick={() => saveMode("annual")} disabled={savingMode} data-testid="accrual-mode-annual" className={`flex items-center gap-2 text-[13px] font-bold px-4 py-2 rounded-sm border transition-colors disabled:opacity-60 ${mode === "annual" ? "bg-purple-700 text-white border-purple-700" : "bg-white text-gray-600 border-gray-300 hover:border-purple-700"}`}><CalendarRange size={15} /> Dotazione annua</button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5" data-testid="hr-email-card">
        <div className="flex items-center gap-2 text-[13px] font-bold text-[#16345c] mb-1"><Mail size={16} className="text-[#0d6cb8]" /> Email ufficio HR (avvisi automatici)</div>
        <p className="text-[12px] text-gray-500 mb-2.5">Indirizzo unico a cui inviare gli avvisi automatici (es. scatti di anzianità). Se vuoto, gli avvisi vengono inviati alle email degli amministratori.</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input type="email" value={hrEmail} data-testid="hr-email-input" onChange={(e) => setHrEmail(e.target.value)} placeholder="es. hr@teleborsa.it" className="flex-1 border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c]" />
          <button onClick={saveHrEmail} disabled={savingHr} data-testid="hr-email-save" className="flex items-center justify-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60"><Save size={15} /> {savingHr ? "Salvataggio..." : "Salva"}</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-6">
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Mese (vuoto = corrente)</label>
          <input type="month" value={runPeriod} data-testid="accrual-period-input" onChange={(e) => setRunPeriod(e.target.value)} className="border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c]" />
        </div>
        <button onClick={() => setConfirm("run")} data-testid="accrual-run-btn" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Play size={15} /> Esegui maturazione mensile</button>
        <button onClick={() => setConfirm("carryover")} data-testid="accrual-carryover-btn" className="flex items-center gap-2 bg-[#0d6cb8] hover:bg-[#0a5590] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><ArrowDownToLine size={15} /> Riporta saldi periodo precedente</button>
        <button onClick={() => setConfirm("reminders")} data-testid="accrual-reminders-btn" className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><BellRing size={15} /> Invia solleciti password</button>
        <button onClick={() => setConfirm("scatti")} data-testid="accrual-scatti-btn" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><TrendingUp size={15} /> Elabora scatti anzianità</button>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6" data-testid="annual-allocation-card">
        <div className="flex items-center gap-2 text-[13px] font-bold text-purple-800 mb-1"><CalendarRange size={16} /> Dotazione annua (accredito monte annuo)</div>
        <p className="text-[12px] text-gray-600 mb-3">Accredita in un'unica soluzione l'intero monte annuo CCNL (ferie, ROL, ex-festività). Idempotente per anno: non accredita due volte lo stesso anno. In modalità "Dotazione annua", a <b>gennaio</b> riceverai un promemoria via email se l'accredito dell'anno non è ancora stato effettuato.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">Anno</label>
            <input type="number" min="2000" max="2100" value={annualYear} data-testid="annual-year-input" onChange={(e) => setAnnualYear(e.target.value)} className="w-[110px] border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-purple-700" />
          </div>
          <label className="flex items-center gap-2 text-[12.5px] text-gray-700 pb-2 cursor-pointer select-none">
            <input type="checkbox" checked={annualProrate} data-testid="annual-prorate-checkbox" onChange={(e) => setAnnualProrate(e.target.checked)} className="w-4 h-4 accent-purple-700" />
            Riproporziona per assunzioni infrannuali (pro-rata mesi restanti)
          </label>
          <button onClick={() => setConfirm("annual")} data-testid="accrual-annual-btn" className="flex items-center gap-2 bg-purple-700 hover:bg-purple-800 text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors"><Play size={15} /> Accredita dotazione annua</button>
        </div>
      </div>

      <h3 className="text-[14px] font-bold text-[#16345c] mb-2">Storico maturazioni</h3>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="accruals-history">
        {runs.length === 0 && <div className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessuna maturazione registrata.</div>}
        {runs.map((r) => {
          const open = expanded === r.id;
          return (
            <div key={r.id}>
              <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                <div onClick={() => setExpanded(open ? null : r.id)} className="flex items-center gap-2 flex-1 cursor-pointer" data-testid={`accrual-row-${r.id}`}>
                  {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                  <div>
                    <div className="text-[13.5px] font-bold text-[#16345c] flex items-center gap-2">
                      {monthLabel(r.period)}
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${(TYPE_BADGE[r.type] || TYPE_BADGE.monthly).cls}`}>{(TYPE_BADGE[r.type] || TYPE_BADGE.monthly).label}</span>
                    </div>
                    <div className="text-[11px] text-gray-500">{fmtDate(r.run_at)} · {r.employees_count} dipendenti · {r.triggered_by === "auto" ? "automatico" : r.triggered_by}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-[12px] text-gray-600 text-right hidden sm:block">
                    +{r.total_ferie} gg · +{r.total_rol} h ROL · +{r.total_exfestivita} h ex-fest
                  </div>
                  <button onClick={() => exportRun(r, "csv")} data-testid={`accrual-export-csv-${r.id}`} title="Esporta CSV" className="w-8 h-8 flex items-center justify-center rounded-sm text-[#009640] hover:bg-green-50 transition-colors"><FileSpreadsheet size={15} /></button>
                  <button onClick={() => exportRun(r, "pdf")} data-testid={`accrual-export-pdf-${r.id}`} title="Esporta PDF" className="w-8 h-8 flex items-center justify-center rounded-sm text-[#0d6cb8] hover:bg-blue-50 transition-colors"><FileText size={15} /></button>
                </div>
              </div>
              {open && (
                <div className="px-4 pb-3 overflow-x-auto">
                  <table className="w-full min-w-[420px] text-[12.5px]">
                    <thead><tr className="text-gray-400 text-[11px] uppercase"><th className="text-left py-1">Dipendente</th><th className="text-right py-1">Ferie</th><th className="text-right py-1">ROL</th><th className="text-right py-1">Ex-fest.</th></tr></thead>
                    <tbody>
                      {(r.entries || []).map((en) => (
                        <tr key={en.employee_id} className="border-t border-gray-50">
                          <td className="py-1 text-gray-700">{en.name} <span className="text-gray-400">({en.matricola})</span>{r.type === "annual" && en.prorata_pct != null && en.prorata_pct < 100 && <span className="ml-1 text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full">pro-rata {en.prorata_pct}%</span>}</td>
                          <td className="py-1 text-right">+{en.ferie}</td>
                          <td className="py-1 text-right">+{en.rol}</td>
                          <td className="py-1 text-right">+{en.exfestivita}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog open={!!confirm} onCancel={() => setConfirm(null)} onConfirm={doAction} confirmLabel={busy ? "Attendere..." : "Conferma"} confirmClass="bg-[#16345c] hover:bg-[#0d1b2f]" title="Confermare l'operazione?">
        {confirm && confirmText[confirm]}
      </ConfirmDialog>
    </div>
  );
};
